<div class="ws-page ws-page-spacious flags-page">
  <header class="page-head">
    <div>
      <p class="kicker">Building enforcement · Violation flags</p>
      <div class="page-head-title-row">
        <app-page-back-btn />
        <h2>Violation flags</h2>
        <app-page-refresh-btn [loading]="loading()" (refresh)="reload()" />
      </div>
      <p class="muted ws-lead">Review compliance findings and record enforcement decisions.</p>
    </div>
  </header>

  <div class="flags-layout">
    <aside class="flags-list-panel page-card">
      <div class="flags-list-head">
        <h3 class="panel-title">{{ listPanelTitle() }}</h3>
        <select
          class="filter"
          [ngModel]="statusFilter()"
          (ngModelChange)="statusFilter.set($event); reload()"
        >
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="under_review">Under review</option>
          <option value="accepted">Accepted</option>
          <option value="rejected">Rejected</option>
          <option value="recheck_requested">Recheck requested</option>
        </select>
      </div>

      <div class="flags-toolbar">
        <p class="count text-meta" *ngIf="loading()">Loading…</p>
        <p class="count error" *ngIf="error()">{{ error() }}</p>
        <p class="count text-meta" *ngIf="!loading() && !error()">
          {{ flags().length }} record(s)<span *ngIf="statusFilter() === '' && openCaseCount() !== flags().length">
            · {{ openCaseCount() }} pending review</span>
        </p>
      </div>

      <p class="ws-empty" *ngIf="!loading() && !flags().length">No violation flags match the selected filter.</p>

      <label class="flags-select-field" *ngIf="!loading() && flags().length">
        <span class="flags-select-label">Select case</span>
        <select
          class="flags-select filter"
          [ngModel]="selectedId()"
          (ngModelChange)="onFlagSelect($event)"
        >
          <option [ngValue]="null">Choose a violation flag…</option>
          <option *ngFor="let f of flags()" [ngValue]="f.id">{{ flagOptionLabel(f) }}</option>
        </select>
      </label>

      <p class="flags-select-summary text-meta" *ngIf="selectedFlag() as f">
        {{ friendlySummary(f.summary) }}
      </p>
    </aside>

    <section class="flags-detail-panel">
      <div class="ws-empty detail-empty" *ngIf="!detail()">
        <h3>Select a violation flag</h3>
        <p class="text-meta">Choose a case from the dropdown to review findings and record a decision.</p>
      </div>

      <ng-container *ngIf="detail() as d">
        <div class="page-card">
          <div class="main-head">
            <div>
              <p class="kicker">Plot {{ d.flag.plotRef }}</p>
              <h2 class="panel-title">{{ friendlySummary(d.flag.summary) }}</h2>
            </div>
            <app-status-badge [statusCd]="d.flag.statusCd" size="lg">{{ statusLabel(d.flag.statusCd) }}</app-status-badge>
          </div>

          <p class="case-closed-note" *ngIf="isClosedCase(d.flag.statusCd)">
            This case is closed. Review history below records the officer decision.
          </p>

          <div class="metrics">
            <article>
              <span class="text-meta">Reference</span>
              <strong>{{ refLabel(d.flag.referenceMode) }}</strong>
            </article>
            <article>
              <span class="text-meta">Confidence</span>
              <div class="confidence-visual" [style.--pct]="confidenceRingPct(d.flag.confidenceScore)">
                <div class="confidence-ring" aria-hidden="true"></div>
                <strong>{{ confidencePct(d.flag.confidenceScore) }}</strong>
              </div>
            </article>
          </div>
        </div>

        <div class="page-card page-card--compact-head">
          <h3 class="panel-title">Compliance findings</h3>
          <div class="table-wrap">
            <table class="data">
              <thead>
                <tr>
                  <th>Rule</th>
                  <th>Measured</th>
                  <th>Allowed</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let v of d.flag.violatedRulesJson || []">
                  <td>
                    <strong class="text-primary" [attr.title]="v.ruleCode">{{ v.ruleName || v.ruleCode }}</strong>
                  </td>
                  <td class="num">{{ v.measured }} {{ v.unit }}</td>
                  <td class="num">{{ v.limit }} {{ v.unit }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="text-meta findings-note">Tolerance and source details available in audit export.</p>
        </div>

        <div class="page-card" [class.disabled]="!canReview(d.flag.statusCd)">
          <h3 class="panel-title">Officer decision</h3>
          <textarea
            rows="3"
            [ngModel]="comment()"
            (ngModelChange)="comment.set($event)"
            placeholder="Officer remark (optional)"
          ></textarea>
          <div class="actions">
            <button type="button" class="btn danger" [disabled]="!canReview(d.flag.statusCd)" (click)="submit('reject')">Reject</button>
            <button type="button" class="btn warn" [disabled]="!canReview(d.flag.statusCd)" (click)="submit('recheck')">Request recheck</button>
            <button type="button" class="btn primary" [disabled]="!canReview(d.flag.statusCd)" (click)="submit('accept')">Accept</button>
          </div>
        </div>

        <div class="page-card">
          <button type="button" class="ws-collapse-toggle" (click)="toggleCompare()">
            <div>
              <h3 class="panel-title">Plan vs measured</h3>
              <p class="text-meta compare-hint" *ngIf="d.plan">Plan {{ d.plan['planNo'] }} · {{ d.plan['statusCd'] }}</p>
            </div>
            <span class="toggle-hint">{{ compareOpen() ? 'Hide' : 'Show' }}</span>
          </button>
          <div class="ws-collapse-panel" *ngIf="compareOpen()">
            <p class="ws-empty" *ngIf="!compareRows().length">Comparison data not available for this case.</p>
            <div class="table-wrap" *ngIf="compareRows().length">
              <table class="data">
                <thead>
                  <tr>
                    <th>Metric</th>
                    <th>Sanctioned plan</th>
                    <th>As-built measured</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let row of compareRows()">
                    <td class="text-primary">{{ row.label }}</td>
                    <td class="num">{{ row.planValue }}{{ row.unit ? ' ' + row.unit : '' }}</td>
                    <td class="num">{{ row.measuredValue }}{{ row.unit ? ' ' + row.unit : '' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="page-card">
          <button type="button" class="ws-collapse-toggle" (click)="toggleHistory()">
            <h3 class="panel-title">Review history</h3>
            <span class="toggle-hint">{{ historyOpen() ? 'Hide' : 'Show' }}</span>
          </button>
          <div class="ws-collapse-panel" *ngIf="historyOpen()">
            <div class="history" *ngIf="d.reviews?.length; else noReviews">
              <div class="history-item" *ngFor="let r of d.reviews">
                <div class="history-top">
                  <strong>{{ decisionLabel(r.decision) }}</strong>
                  <span class="text-meta">{{ r.reviewerUid }}</span>
                </div>
                <p>{{ r.comment || 'No remark recorded' }}</p>
                <small class="text-meta">{{ r.decidedAt | date: 'medium' }}</small>
              </div>
            </div>
            <ng-template #noReviews><p class="text-meta">No review decisions recorded.</p></ng-template>
          </div>
        </div>
      </ng-container>
    </section>
  </div>
</div>

<div class="toast" *ngIf="toast()">{{ toast() }}</div>
